// @flow
import React, {useState, useMemo, useEffect, useCallback} from 'react';
import {useLocation, Link, Switch, Route} from 'react-router-dom';
import {
  makeStyles,
  FormControlLabel,
  Checkbox,
  IconButton,
  Typography,
  Grid,
  Divider,
  Snackbar,
} from '@material-ui/core';
import {ArrowBack, Add, Edit} from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';
import {colors} from '../../../../assets/styles/colors';
import {useStoreCountry} from '../../../country/utils';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CardRateShipping from './components/CardRateShipping';
import MemoRateEstandar from '../logistics/components/RateEstandar';
import MemoRateManual from '../logistics/components/RateManual';
import MemoRateAdvanced from '../logistics/components/RateAdvanced';
import MemoRateDefined from '../logistics/components/RateDefined';
import InternationalCountriesModal from './components/InternationalCountriesModal';
import LogisticsShippingStardard from './estandar-shipping/LogisticsShippingStardard';
import LogisticsShippingAdvanced from './advanced-shipping/LogisticsShippingAdvanced';
import {
  useFetchShippingMethods,
  useResetState,
  useUpdateEnabledShippingMethods,
  useUpdateShippingCountries,
  useUpdateShippingMethodActive,
} from './utils';
import ActivateRateModal from './components/ActivateRateModal';
import LogisticsShippingManual from './manual-shipping/LogisticsShippingManual';
import LogisticsShippingDefault from './default-shipping/LogisticsShippingDefault';
import type {Country} from '../../../../entities';
import {customErrorMessage} from '../logistics/utils/customErrorMessage';

type SubRoute = {
  img: () => any,
  name: string,
  description: string,
  path: string,
  enabled: boolean,
  disabled?: boolean,
  component?: () => any,
  id?: string,
  hide?: boolean,
  id2?: string,
  type: string,
};

export const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  endIcon: {
    marginLeft: 'auto',
    color: colors.grey800,
    // fontSize: '0.6rem',
  },
  subRouteHeader: {
    display: 'flex',
    alignItems: 'center',
    // margin: theme.spacing(2, 0),
    paddingInline: theme.spacing(2),
    paddingBlock: theme.spacing(2),
    backgroundColor: 'white',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
    borderLeft: '1px solid #dbdbe0',
    borderRight: '1px solid #dbdbe0',
    borderTop: '1px solid #dbdbe0',
  },
  subRouteTitle: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    fontWeight: 600,
  },
  wrapEnable: {
    padding: theme.spacing(1, 3),
    backgroundColor: 'white',
    borderLeft: '1px solid #dbdbe0',
    borderRight: '1px solid #dbdbe0',
  },
  Enabledisabled: {
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
  },
  checkboxLabel: {
    fontSize: '14px',
    lineHeight: '22px',
    color: '#717190',
  },
  zone: {
    marginBlock: theme.spacing(2),
  },
  zoneTitles: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  subTitleZone: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey600,
  },
  contentZone: {
    display: 'flex',
    flexDirection: 'column',
    marginBlock: theme.spacing(1),
  },
  rowContentZoneInternational: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rowContentZone: {
    display: 'flex',
    alignItems: 'center',
  },
  rowContentZoneCountries: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  textZone: {
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  textZoneSpan: {
    fontSize: '0.875rem',
    fontWeight: 400,
    color: colors.grey800,
    whiteSpace: 'pre-wrap',
  },
  rateShipping: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',

    '& button': {
      color: 'red',
    },
  },
  rateShippingText: {
    fontSize: '1rem',
    fontWeight: 500,
  },
  containerCountries: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 3),
    backgroundColor: 'white',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
    borderLeft: '1px solid #dbdbe0',
    borderRight: '1px solid #dbdbe0',
    borderBottom: '1px solid #dbdbe0',
  },
  divider: {
    background: '#e8e8ed',
    width: '100%',
    // marginBottom: theme.spacing(2),
  },
  iconButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3f0ff',
    borderRadius: '50%',
    padding: '0.2rem',

    '& svg': {
      fontSize: '1rem',
    },
  },
  typeRates: {
    padding: theme.spacing(2, 3),
    backgroundColor: 'white',
    borderRadius: '12px',
    marginTop: theme.spacing(2),
    boxShadow:
      '1px 0px 1px rgba(40, 41, 61, 0.08), -1px 0px 2px rgba(96, 97, 112, 0.16)',
  },
}));

export default function LogisticShipping() {
  const country: Country = useStoreCountry();
  const classes = useStyles();
  const location = useLocation();

  const mainRoute =
    location && location.pathname === '/configure/logistics/shipping';

  const [enabled, setEnabled] = useState(false);
  const [prevEnabled, setPrevEnabled] = useState(false);
  const [typeRateSelected, setTypeRateSelected] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertErrorOpen, setAlertErrorOpen] = useState(false);
  const [showModalCountries, setShowModalCountries] = useState(false);
  const {
    shippingMethods,
    availableShippingCountries,
  } = useFetchShippingMethods();
  const {
    messageError,
    savingMethodSuccess,
    savingMethodError,
  } = useUpdateShippingMethodActive();
  const {
    updateEnabledShippingMethods,
    isSaving,
    savingSuccess,
    savingError,
  } = useUpdateEnabledShippingMethods();
  const {
    savingCountriesSuccess,
    savingCountriesError,
  } = useUpdateShippingCountries();
  const {resetState} = useResetState();
  const [isModalEdit, setIsModalEdit] = useState(false);
  const [showModalActivateRate, setShowModalActivateRate] = useState(false);

  const isMethodShippingEnabled = useCallback(
    (methodType: string) => {
      if (
        !shippingMethods?.storeShippingMethodSettings?.storeShippingMethodType
      ) {
        return false;
      }
      return (
        shippingMethods.storeShippingMethodSettings.storeShippingMethodType ===
        methodType
      );
    },
    [shippingMethods]
  );

  const handleToggleEnabled = event => {
    const newEnabled = event.target.checked;
    setEnabled(newEnabled);
    setPrevEnabled(enabled);

    updateEnabledShippingMethods(newEnabled);
  };

  const subRoutes: {[key: string]: SubRoute} = useMemo(
    () => ({
      standard: {
        img: MemoRateEstandar,
        name: 'Tarifa Estándar',
        description: 'Una tarifa nacional y una tarifa internacional.',
        path: '/configure/logistics/shipping/standard',
        enabled: isMethodShippingEnabled('STANDARD'),
        component: LogisticsShippingStardard,
        id: 'standard-shipping',
        id2: 'standard-shipping-configuration',
        type: 'STANDARD',
      },
      manual: {
        img: MemoRateManual,
        name: 'Tarifa Manual',
        description:
          'Crea grupo de tarifas para que el usuario seleccione la ideal.',
        path: '/configure/logistics/shipping/manual',
        enabled: isMethodShippingEnabled('FLAT_RATE'),
        component: LogisticsShippingManual,
        id: 'manual-shipping',
        id2: 'manual-shipping-configuration',
        type: 'FLAT_RATE',
      },
      advanced: {
        img: MemoRateAdvanced,
        name: 'Tarifa Avanzada',
        description: 'Tarifas automáticas en base a la zona de reparto.',
        path: '/configure/logistics/shipping/advanced',
        enabled: isMethodShippingEnabled('ADVANCED'),
        component: LogisticsShippingAdvanced,
        id: 'advanced-shipping',
        id2: 'advanced-shipping-configuration',
        type: 'ADVANCED',
      },
      default: {
        img: MemoRateDefined,
        name: 'Definir al momento de la compra',
        description: '',
        path: '/configure/logistics/shipping/default',
        enabled: isMethodShippingEnabled('MANUAL'),
        component: LogisticsShippingDefault,
        id: 'default-shipping',
        id2: 'default-shipping-configuration',
        type: 'MANUAL',
      },
    }),
    [isMethodShippingEnabled]
  );

  const subRoutesArray = Object.keys(subRoutes)
    .map(k => subRoutes[k])
    .filter((route: SubRoute) => !route.hide);

  const subRoute: ?SubRoute = subRoutesArray.find(
    (route: SubRoute) => location.pathname === route.path
  );

  useEffect(() => {
    if (shippingMethods) {
      setEnabled(shippingMethods.enabled);
      setPrevEnabled(shippingMethods.enabled);
    }
  }, [shippingMethods]);

  useEffect(() => {
    if (savingError) {
      setEnabled(prevEnabled);
    }
  }, [savingError, prevEnabled]);

  useEffect(() => {
    if (savingSuccess) {
      setAlertOpen(true);
    }

    if (savingError || savingCountriesError || savingMethodError) {
      setAlertErrorOpen(true);
    }
  }, [
    savingCountriesError,
    savingCountriesSuccess,
    savingError,
    savingSuccess,
    savingMethodSuccess,
    savingMethodError,
  ]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  return (
    <div className={classes.root}>
      <Snackbar
        open={alertOpen}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
      >
        <Alert severity="success" onClose={() => setAlertOpen(false)}>
          Configuración guardada correctamente
        </Alert>
      </Snackbar>
      <Snackbar
        open={alertErrorOpen}
        autoHideDuration={4000}
        onClose={() => setAlertErrorOpen(false)}
      >
        <Alert severity="error" onClose={() => setAlertErrorOpen(false)}>
          {customErrorMessage(messageError)}
        </Alert>
      </Snackbar>

      {subRoute && (
        <>
          <div className={classes.subRouteHeader}>
            <IconButton
              variant="contained"
              size="small"
              component={Link}
              to={`/configure/logistics/shipping${window.location.search}`}
            >
              <ArrowBack />
            </IconButton>
            <Typography className={classes.subRouteTitle} id={subRoute.id2}>
              {subRoute.name}
            </Typography>
          </div>
          <Divider className={classes.divider} />
        </>
      )}

      <Switch>
        <Route exact path="/configure/logistics/shipping">
          <Divider className={classes.divider} />
          {mainRoute && (
            <div
              className={`${classes.wrapEnable} ${
                !enabled ? classes.Enabledisabled : ''
              }`}
            >
              <FormControlLabel
                classes={{label: classes.checkboxLabel}}
                control={
                  <Checkbox
                    checked={enabled}
                    onChange={handleToggleEnabled}
                    color="primary"
                    disabled={isSaving}
                  />
                }
                label="Activar configuraciones de envío."
              />
            </div>
          )}

          {enabled && (
            <>
              <div className={classes.containerCountries}>
                <div className={classes.zone}>
                  <div className={classes.zoneTitles}>
                    <Typography className={classes.subTitleZone}>
                      ZONAS DE ENVÍOS ACTIVADAS
                    </Typography>
                    {availableShippingCountries &&
                      availableShippingCountries.filter(
                        availableCountry =>
                          availableCountry.code !== country?.code
                      ).length === 0 && (
                        <CustomButton
                          endIcon={
                            <div className={classes.iconButton}>
                              <Add />
                            </div>
                          }
                          color="primary"
                          variant="text"
                          onClick={() => setShowModalCountries(true)}
                        >
                          Agregar envios internacionales
                        </CustomButton>
                      )}
                  </div>
                  <div className={classes.contentZone}>
                    <Typography className={classes.textZone}>
                      Nacional
                    </Typography>
                    <Typography className={classes.textZoneSpan}>
                      {country && country.name}
                    </Typography>
                  </div>
                  {availableShippingCountries &&
                    availableShippingCountries.filter(
                      availableCountry =>
                        availableCountry.code !== country?.code
                    ).length > 0 && (
                      <div className={classes.contentZone}>
                        <div className={classes.rowContentZoneInternational}>
                          <Typography className={classes.textZone}>
                            Internacional
                          </Typography>
                          <CustomButton
                            color="primary"
                            variant="text"
                            startIcon={
                              <div className={classes.iconButton}>
                                <Edit />
                              </div>
                            }
                            onClick={() => {
                              setIsModalEdit(true);
                              setShowModalCountries(true);
                            }}
                          >
                            Editar
                          </CustomButton>
                        </div>
                        <div className={classes.rowContentZoneCountries}>
                          <Typography className={classes.textZoneSpan}>
                            {availableShippingCountries &&
                              availableShippingCountries
                                .filter(countryI => countryI.id !== country?.id)
                                .map(country => country.name)
                                .join(' - ')}
                          </Typography>
                        </div>
                      </div>
                    )}
                </div>
              </div>
              <div className={classes.typeRates}>
                <div className={classes.rateShipping}>
                  <Typography className={classes.rateShippingText}>
                    Seleccionar que tipo de tarifa quieres configurar
                  </Typography>
                  <Typography className={classes.textZoneSpan}>
                    Solo se puede usar un tipo de tarifa a la vez
                  </Typography>
                </div>
                <Grid container spacing={2}>
                  {subRoutesArray.map((route: SubRoute, idx: number) => (
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      md={4}
                      lg={3}
                      key={`shipping-button-${idx}`}
                    >
                      <CardRateShipping
                        enabled={route.enabled}
                        componentImg={route.img}
                        title={route.name}
                        description={route.description}
                        to={`${route.path}${window.location.search}`}
                        setTypeRateSelected={setTypeRateSelected}
                        typeRate={route.type}
                        handleOpenModalActivate={() =>
                          setShowModalActivateRate(true)
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </div>
            </>
          )}
        </Route>
        {subRoutesArray.map((route: SubRoute, idx: number) => (
          <Route key={idx} path={route.path} component={route.component} />
        ))}
      </Switch>

      {showModalCountries && (
        <InternationalCountriesModal
          open={showModalCountries}
          // $FlowFixMe
          country={country}
          edit={isModalEdit}
          handleClose={() => {
            setShowModalCountries(false);
            setIsModalEdit(false);
          }}
          availableShippingCountries={availableShippingCountries}
        />
      )}

      {showModalActivateRate && (
        <ActivateRateModal
          open={showModalActivateRate}
          setTypeRateSelected={setTypeRateSelected}
          typeRateSelected={typeRateSelected}
          handleClose={() => {
            setShowModalActivateRate(false);
          }}
        />
      )}
    </div>
  );
}
